'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import qs from 'qs'
import { dissoc } from 'ramda'

import {
  CustomerTypes,
  extractCustomerFromClientSession,
  extractTokenFromClientSession
} from 'modules/customer'
import {
  Brand,
  CategoryTags,
  CategoryTypeWithCounter,
  getNextEntityPage,
  getProductsByCategories,
  getProperProductForm,
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValue,
  getUniqueCategories,
  ListingCategoryType,
  mergeAndCustomizeSearchCategories,
  PageCategoryType,
  parseQueryFilters,
  ProductCard,
  ProductCategoryType,
  ProductMetaTypes,
  ProductTypes,
  SORTING_OPTIONS,
  SortingOptionsTypes,
  useInfiniteScroll,
  viewProductList
} from 'modules/products'
import { SeoListName, seoListNameDynamic } from 'shared/analytics'
import BackToTopButton from 'shared/ui/BackToTopButton'
import Breadcrumbs from 'shared/ui/Breadcrumbs'
import SelectDropdown from 'shared/ui/form/Select'
import InfiniteScrollAnchor from 'shared/ui/InfiniteScrollAnchor'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { createAsPath, searchParamsToObject } from 'shared/utils/searchParams'
import { addTestId } from 'shared/utils/tests'

import { useQueryCategories } from '../../../queries/useQueryCategories'

import styles from './BrandPage.module.scss'

const BrandPage = ({
  products,
  productsMeta,
  productsBrand,
  slug,
  distributionCenterId
}: {
  products: ProductTypes[]
  productsMeta: ProductMetaTypes
  productsBrand: Brand
  slug: string
  distributionCenterId: string
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [filteredCategories, setFilteredCategories] = useState<
    | CategoryTypeWithCounter<
        PageCategoryType | ListingCategoryType | ProductCategoryType
      >[]
    | null
  >(null)
  const [filteredProducts, setFilteredProducts] = useState<ProductTypes[]>([])
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)

  const [sorting, setSorting] = useState<string | string[]>('recommended')
  const router = useRouter()
  const pathname = usePathname()
  const urlSearchParams = useSearchParams()

  // Convert URLSearchParams to plain object for compatibility
  const query = searchParamsToObject(urlSearchParams)

  // Create asPath equivalent for App Router
  const asPath = createAsPath(pathname, urlSearchParams)

  const token = extractTokenFromClientSession(session)

  const { isFetching, productsData, ref } = useInfiniteScroll({
    products,
    query,
    slug,
    distributionCenterId,
    getNextPageCallback: getNextEntityPage,
    token,
    limit: PRODUCTS_LISTING_LIMIT
  })
  const { data: categories } = useQueryCategories(session)

  const categoriesOptions = [...(categories || []).filter(cat => cat.isVisible)]

  const { total } = productsMeta

  useEffect(() => {
    const sortingToCheck = {
      sort_by: query.sort_by,
      order: query.order
    } as SortingOptionsTypes

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
  }, [query])

  useEffect(() => {
    setFilteredCategories(getUniqueCategories(products))
    if (selectedCategory !== 'all') {
      setFilteredProducts(
        getProductsByCategories(productsData || [], selectedCategory)
      )
    } else {
      setFilteredProducts(productsData || [])
    }
  }, [productsData, selectedCategory])

  const handleSortingChange = (_, value: string | string[]) => {
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValue(value)

    const parsedQuery = parseQueryFilters(query)

    // @sieradz - if they are recommended then we don't sort
    // (from the API we get a ready list), so we remove
    // from the query - rest order and sort_by
    if (value === 'recommended') {
      delete parsedQuery?.order
      delete parsedQuery?.sort_by
    }

    const params = qs.stringify(
      { ...dissoc('entity', parsedQuery), ...sortingOptions },
      { addQueryPrefix: true }
    )

    const entity = parsedQuery?.entity || []

    router.replace(`${entity.join('/')}${decodeURIComponent(params)}`)
  }

  const handleCategoryChange = (value: string) => {
    if (selectedCategory === value) {
      setSelectedCategory('all')
    } else setSelectedCategory(value)
  }

  useEffect(() => {
    if (session?.status !== 'loading' && isNotNilOrEmpty(products)) {
      viewProductList({
        user,
        products,
        listName: seoListNameDynamic(
          SeoListName.BRAND_PAGE_LIST_NAME,
          productsBrand.name
        )
      })
    }
  }, [session])

  const categorySlug =
    typeof productsBrand.slug === 'string'
      ? productsBrand.slug
      : productsBrand.slug?.slug || ''

  const breadcrumbs = [
    { label: 'Wszyscy wytwórcy', path: PATHS.brand },
    {
      label: productsBrand.name,
      path: `${PATHS.brand}/${categorySlug}`
    }
  ]

  const isInformationHeaderVisible =
    isNotNilOrEmpty(productsBrand.descriptionShort) ||
    isNotNilOrEmpty(productsBrand.descriptionPrimary) ||
    isNotNilOrEmpty(productsBrand.descriptionSecondary)

  return (
    <>
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </div>
      {isInformationHeaderVisible && (
        <div className={styles.header}>
          <div className={styles.brandInfoWrapper}>
            <div className={styles.mainBrandInfoWrapper}>
              <div className={styles.brandBannerWrapper}>
                {productsBrand.brandImage && (
                  <Image
                    src={productsBrand.brandImage}
                    alt={productsBrand.name}
                    fill
                    style={{ objectFit: 'contain', objectPosition: 'left' }}
                  />
                )}
              </div>
              <div className={styles.brandDescriptionWrapper}>
                {productsBrand.brandLogo && (
                  <div className={styles.brandLogoWrapper}>
                    <Image
                      src={productsBrand.brandLogo}
                      alt={productsBrand.name}
                      fill
                      style={{ objectFit: 'contain', objectPosition: 'left' }}
                    />
                  </div>
                )}
                {productsBrand.descriptionShort && (
                  <p className={styles.infoDescription}
                    dangerouslySetInnerHTML={{
                      __html: productsBrand.descriptionShort
                    }}
                  />
                )}
                {productsBrand.descriptionPrimary && (
                  <p className={styles.infoDescription}
                    dangerouslySetInnerHTML={{
                      __html: productsBrand.descriptionPrimary
                    }}
                  />
                )}
              </div>
            </div>
            {productsBrand.descriptionSecondary && (
              <p className={styles.infoDescription}
                dangerouslySetInnerHTML={{
                  __html: productsBrand.descriptionSecondary
                }}
              />
            )}
          </div>
        </div>
      )}
      <CategoryTags
        categories={mergeAndCustomizeSearchCategories(
          categoriesOptions,
          filteredCategories
        )}
        mainCategory={categoriesOptions.find(
          cat => selectedCategory === String(cat.code)
        )}
        onClick={(_, code) => {
          const selected = code as string

          handleCategoryChange(selected)
        }}
      />
      <div className={styles.header}>
        <h1 className={styles.title}>
          {productsBrand.name}{' '}
          <span className={styles.numberOfProducts}>({getProperProductForm(total)})</span>
        </h1>

        <div className={styles.sortingInputWrapper} {...addTestId('sorting-input')}>
          <div className={styles.sortingLabel}>Sortuj:</div>
          <SelectDropdown
            id='brand-page-sorting'
            variant='ghost'
            noMargin
            options={SORTING_OPTIONS}
            name='sorting'
            value={sorting}
            onChange={handleSortingChange}
          />
        </div>
      </div>
      <span data-title='top' />
      <div className={styles.wrapper}>
        <BackToTopButton target='*[data-title="top"]' />
        {filteredProducts &&
          filteredProducts.map((product: ProductTypes, idx) => {
            return (
              <ProductCard
                productPath={product?.slug || ''}
                key={product.id + idx.toString()}
                product={product}
                ageLimitMetCategory={true}
                seoButtonPlace={asPath}
                listName={seoListNameDynamic(
                  SeoListName.BRAND_PAGE_LIST_NAME,
                  productsBrand.name
                )}
                idxOnList={idx}
                id={`brand_page_product_${product.id}`}
              />
            )
          })}
        <InfiniteScrollAnchor ref={ref}></InfiniteScrollAnchor>
      </div>
      {isFetching && (
        <div className={styles.loaderWrapper}>
          <LoadingIndicator />
        </div>
      )}
    </>
  )
}

export default BrandPage
